import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { useEffect, useState } from "react";
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  LayoutDashboard,
  Eye,
  Clock,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { AuthGate } from "@/components/auth-gate";
import { stripeService } from "@/lib/services/stripeService";
import { supabase } from "@/integrations/supabase/client";
import type { Review } from "@/lib/db/models";

const search = z.object({
  billing_id: fallback(z.string().optional(), undefined),
  session_id: fallback(z.string().optional(), undefined),
  demo: fallback(z.string().optional(), undefined),
  review: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/billing/success")({
  head: () => ({
    meta: [{ title: "ClaimGuard | Payment received" }],
  }),
  validateSearch: zodValidator(search),
  component: () => (
    <AuthGate>
      <BillingSuccessPage />
    </AuthGate>
  ),
});

type OkDetails = {
  review: Review | null;
  invoiceNumber: string | null;
  kind: "review_fee" | "fasttrack_fee" | "negotiation_fee" | null;
};

const REDIRECT_SECONDS = 5;

function BillingSuccessPage() {
  const { billing_id, session_id, demo, review } = Route.useSearch();
  const navigate = useNavigate();
  const [state, setState] = useState<"working" | "ok" | "pending" | "error">("working");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [details, setDetails] = useState<OkDetails>({
    review: null,
    invoiceNumber: null,
    kind: null,
  });
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [redirectActive, setRedirectActive] = useState(true);

  /* ── Fetch review details once payment is confirmed ── */
  useEffect(() => {
    if (state !== "ok" || !billing_id) return;
    let mounted = true;
    (async () => {
      const { data: bill } = await supabase
        .from("billing_records")
        .select("review_id,kind")
        .eq("id", billing_id)
        .maybeSingle();
      if (!mounted || !bill) return;

      let reviewData: Review | null = null;
      let invNum: string | null = null;

      if (bill.review_id) {
        const { data: r } = await supabase
          .from("reviews")
          .select("*")
          .eq("id", bill.review_id)
          .maybeSingle();
        if (mounted && r) reviewData = r as Review;

        const { data: inv } = await supabase
          .from("invoice_data")
          .select("extracted")
          .eq("review_id", bill.review_id)
          .maybeSingle();
        if (mounted && inv?.extracted) {
          const extracted = inv.extracted as Record<string, unknown>;
          const num = extracted.invoice_number;
          if (typeof num === "string" && num.trim()) invNum = num.trim();
        }
      }

      if (mounted) {
        setDetails({
          review: reviewData,
          invoiceNumber: invNum,
          kind: (bill.kind as OkDetails["kind"]) ?? null,
        });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [state, billing_id]);

  /* ── Payment confirmation (original logic preserved) ── */
  useEffect(() => {
    let mounted = true;
    if (!billing_id) {
      setState("ok");
      return;
    }
    stripeService
      .confirmPaid({
        billingId: billing_id,
        sessionId: session_id,
        demo: demo === "1",
      })
      .then((r) => {
        if (!mounted) return;
        if (!r.ok) {
          setState("pending");
          return;
        }
        if (r.negotiation_error) {
          console.error("[billing.success] negotiation provisioning failed", r.negotiation_error);
          setErrorMsg(r.negotiation_error);
          setState("error");
          return;
        }
        setState("ok");
        if (r.negotiation_id && r.review_file_number) {
          navigate({ to: "/negotiation", search: { review: r.review_file_number } });
        }
      })
      .catch((e) => {
        if (!mounted) return;
        setErrorMsg((e as Error).message);
        setState("error");
      });
    return () => {
      mounted = false;
    };
  }, [billing_id, session_id, demo, navigate]);

  /* ── Auto-redirect countdown ── */
  useEffect(() => {
    if (state !== "ok" || !redirectActive) return;
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(id);
          navigate({ to: "/dashboard" });
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [state, redirectActive, navigate]);

  const isDemo = demo === "1";
  const fileNumber = details.review?.file_number ?? review ?? null;
  const isFastTrack =
    details.kind === "fasttrack_fee" || details.review?.fast_track === true;

  const estimatedCompletion = isFastTrack
    ? "Within 24 hours"
    : "2–3 business days";

  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />

      <main className="container-page py-12 md:py-20">
        <div className="mx-auto max-w-2xl">
          {/* ── Working ── */}
          {state === "working" && (
            <div className="surface-card p-10 text-center">
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-trust" />
              <h1 className="mt-5 font-display text-2xl text-navy">
                Finalizing your payment…
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Please wait while we confirm everything on our end.
              </p>
            </div>
          )}

          {/* ── OK ── */}
          {state === "ok" && (
            <div className="space-y-8">
              {/* Hero */}
              <section className="text-center">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-savings/10 text-savings shadow-sm">
                  <CheckCircle2 className="h-10 w-10" />
                </div>
                <h1 className="mt-6 font-display text-4xl text-navy md:text-5xl">
                  {isDemo ? "Demo payment recorded" : "Payment Received"}
                </h1>
                <p className="mx-auto mt-3 max-w-md text-base text-muted-foreground md:text-lg">
                  {isDemo
                    ? "Stripe isn't configured yet, so no real charge was made. A billing record has been saved so we can wire it up when you're ready."
                    : "Your report is being prepared. We'll notify you as soon as it's ready on your dashboard."}
                </p>
              </section>

              {/* Confirmation Card */}
              <section className="surface-card overflow-hidden">
                <div className="border-b border-border bg-secondary/30 px-6 py-4">
                  <h2 className="font-display text-lg text-navy">Confirmation</h2>
                </div>
                <div className="grid gap-px bg-border sm:grid-cols-2">
                  <DetailRow label="Payment Status" value="Paid" tone="savings" />
                  <DetailRow label="Review Status" value="Now Processing" tone="trust" />
                  <DetailRow
                    label="Invoice Number"
                    value={details.invoiceNumber ?? "—"}
                  />
                  <DetailRow label="Review ID" value={fileNumber ?? "—"} />
                  <DetailRow
                    label="Estimated Completion"
                    value={estimatedCompletion}
                  />
                  <DetailRow
                    label="Priority Level"
                    value={isFastTrack ? "FastTrack" : "Standard"}
                    tone={isFastTrack ? "fasttrack" : undefined}
                  />
                </div>
              </section>


              {/* Actions */}
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/dashboard"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-navy px-6 py-3.5 text-sm font-semibold text-navy-foreground shadow-sm transition hover:opacity-90 hover:shadow-md"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Go to My Dashboard
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {fileNumber && (
                  <button
                    onClick={() =>
                      navigate({ to: "/review", search: { id: fileNumber } })
                    }
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-white px-6 py-3.5 text-sm font-semibold text-navy transition hover:bg-secondary/50"
                  >
                    <Eye className="h-4 w-4" />
                    Watch Live Review Progress
                  </button>
                )}
              </div>

              {/* Reassurance */}
              <div className="rounded-xl border border-savings/20 bg-savings-soft/40 p-5 text-center">
                <p className="text-sm font-medium text-navy">
                  You do not need to stay on this page.
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Your review continues securely in the background. We'll notify you
                  when your report is ready.
                </p>
              </div>

              {/* Auto redirect */}
              {redirectActive ? (
                <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-white p-5 text-center shadow-sm">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Returning you to your dashboard in{" "}
                    <span className="inline-block min-w-[1.2em] font-mono font-semibold text-navy">
                      {countdown}
                    </span>{" "}
                    seconds.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate({ to: "/dashboard" })}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-navy px-4 py-2 text-xs font-semibold text-navy-foreground transition hover:opacity-90"
                    >
                      Go Now <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setRedirectActive(false)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-xs font-semibold text-navy transition hover:bg-secondary/50"
                    >
                      Stay Here
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <button
                    onClick={() => navigate({ to: "/dashboard" })}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-trust transition hover:underline"
                  >
                    Go to My Dashboard <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Pending ── */}
          {state === "pending" && (
            <div className="surface-card p-10 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-trust-soft text-trust">
                <Clock className="h-7 w-7" />
              </div>
              <h1 className="mt-5 font-display text-2xl text-navy">Payment pending</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                We haven't confirmed your payment yet. If you completed checkout,
                this page will update shortly.
              </p>
              <div className="mt-6">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 rounded-lg bg-trust px-5 py-2.5 text-sm font-semibold text-trust-foreground hover:opacity-90"
                >
                  Go to My Dashboard <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}

          {/* ── Error ── */}
          {state === "error" && (
            <div className="surface-card p-10 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <span className="text-2xl font-semibold">!</span>
              </div>
              <h1 className="mt-5 font-display text-2xl text-navy">
                Something went wrong
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                We couldn't finish setting up your service. Please contact support
                if you were charged.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/support"
                  className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground hover:opacity-90"
                >
                  Contact Support
                </Link>
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:bg-secondary/50"
                >
                  Go to Dashboard
                </Link>
              </div>
              {errorMsg && (
                <pre className="mt-6 whitespace-pre-wrap break-words rounded-lg bg-secondary/60 p-3 text-left text-xs text-navy">
                  {errorMsg}
                </pre>
              )}
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

/* ─── Helpers ─── */

function DetailRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "savings" | "trust" | "fasttrack";
}) {
  const valueCls =
    tone === "savings"
      ? "text-savings"
      : tone === "trust"
        ? "text-trust"
        : tone === "fasttrack"
          ? "text-fasttrack"
          : "text-navy";
  return (
    <div className="flex items-center justify-between bg-white px-6 py-3.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${valueCls}`}>{value}</span>
    </div>
  );
}
